import { Children, cloneElement, isValidElement, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';

import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';

import { useStudentRegistration } from 'contexts/StudentRegistrationContext';
import { getClient, getClientRegistration } from 'services/api/clients';
import { intakeToEnquiryForm } from 'utils/registrationTransform';

// Reverse of the createInquiry mapping in pages/inclusionhead/enquiry-form.jsx:
// when the form's verbatim blob hasn't been written yet, rebuild a best-effort
// camelCase blob from the structured inquiry row so the EnquiryForm prefills.
function synthesizeEnquiryFromInquiry(inq) {
  if (!inq || typeof inq !== 'object') return {};
  const synth = {};
  if (inq.source) synth.source = inq.source;
  if (inq.source_detail) synth.sourceDetails = inq.source_detail;
  if (inq.primary_diagnosis) synth.primaryConcern = inq.primary_diagnosis;
  const services = inq.preferred_services || {};
  if (services.type) synth.lookingFor = services.type;
  if (services.sub) synth.subOptions = services.sub;
  if (inq.notes) synth.comments = inq.notes;
  if (inq.inquiry_date) synth.enquiryDate = inq.inquiry_date;
  if (inq.status) synth.status = inq.status;

  const childName = `${inq.child_first_name || ''} ${inq.child_last_name || ''}`.trim();
  if (childName) synth.childName = childName;
  if (inq.child_date_of_birth) synth.dateOfBirth = inq.child_date_of_birth;
  if (inq.child_gender) synth.gender = inq.child_gender;

  const fatherName = `${inq.parent_first_name || ''} ${inq.parent_last_name || ''}`.trim();
  if (fatherName) synth.fatherName = fatherName;
  if (inq.parent_phone) synth.fatherMobile = inq.parent_phone;
  if (inq.parent_email) synth.fatherEmail = inq.parent_email;
  if (inq.parent_alternate_phone) synth.motherMobile = inq.parent_alternate_phone;

  if (inq.address_line1) synth.streetAddress = inq.address_line1;
  if (inq.city) synth.city = inq.city;
  if (inq.state) synth.state = inq.state;
  if (inq.postal_code) synth.zipCode = inq.postal_code;
  return synth;
}

/**
 * Hydrates the StudentRegistration context with a client's saved
 * enquiry / intake / IEP data, then renders the wrapped form component.
 *
 * Used by the therapist's form routes so that opening
 * `/therapist/child/:childId/iep` (or intake / enquiry) prefills the
 * form fields with whatever the inclusion-head / supervisor saved.
 *
 * Also injects `returnPath` into the child so Save/Back return to the
 * therapist's Child Profile instead of the inclusion-head workflow page.
 *
 * Mirrors the resume logic in `pages/supervisor/add-student.jsx`.
 */
export default function HydratedReadOnlyForm({ children }) {
  const { childId } = useParams();
  const { updateSharedData, saveFormData, markFormCompleted } = useStudentRegistration();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const hydrate = async () => {
      if (!childId) {
        setLoading(false);
        return;
      }

      try {
        const [client, registration] = await Promise.all([
          getClient(childId).catch(() => null),
          getClientRegistration(childId).catch(() => null)
        ]);
        if (cancelled) return;

        // Mark completed sections so children rendering relies on them work.
        const status = registration?.status || {};
        if (status.enquiryForm) markFormCompleted('enquiryForm');
        if (status.intakeForm) markFormCompleted('intakeForm');
        if (status.iep) markFormCompleted('iep');

        // Hydrate each form's verbatim JSONB blob so the form fields prefill.
        // The verbatim blob is only populated after a "resume-mode" save (i.e.
        // a second save through Continue Onboarding). On first view, fall back
        // to synthesizing the blob from the original inquiry_data columns —
        // otherwise fields like source/sourceDetails/subOptions/comments stay
        // blank because the structured table loses field-name fidelity.
        const enquiryBlob =
          registration?.enquiry_form_data && typeof registration.enquiry_form_data === 'object' ? registration.enquiry_form_data : {};
        // Two synthesised sources under the blob, weakest first. A child registered
        // while Enquiry and Intake were one form has neither a blob nor an inquiry
        // row - its enquiry answers sit in the intake's structured columns, and
        // without intakeToEnquiryForm the read-only enquiry opened blank for them.
        const enquirySynth = {
          ...(intakeToEnquiryForm(registration?.intake_data) || {}),
          ...synthesizeEnquiryFromInquiry(registration?.inquiry_data)
        };
        saveFormData('enquiryForm', { ...enquirySynth, ...enquiryBlob });

        // Already split and de-duplicated by the API, so the intake viewer shows
        // only what the intake actually asked for.
        if (registration?.intake_form_data && typeof registration.intake_form_data === 'object') {
          saveFormData('intakeForm', registration.intake_form_data);
        }
        if (registration?.iep_data && typeof registration.iep_data === 'object') {
          saveFormData('iep', registration.iep_data);
        }

        // Build shared-data updates from inquiry / intake / client so any
        // form field mapped through FIELD_MAPPINGS gets a sensible value.
        const updates = { clientId: childId };
        if (registration?.inquiry_id) updates.inquiryId = registration.inquiry_id;
        if (registration?.intake_id) updates.intakeId = registration.intake_id;

        const intakeCd = registration?.intake_data?.child_demographics || {};
        const childIdFromIntake = intakeCd.child_id;
        const childIdFromIep = registration?.iep_data?.childId;
        const externalChildId = childIdFromIntake || childIdFromIep;
        if (externalChildId) updates.childId = externalChildId;

        const inq = registration?.inquiry_data;
        if (inq) {
          const childName = `${inq.child_first_name || ''} ${inq.child_last_name || ''}`.trim();
          if (childName) updates.childName = childName;
          if (inq.child_date_of_birth) updates.dateOfBirth = inq.child_date_of_birth;
          if (inq.child_gender) updates.gender = inq.child_gender;
          const fatherName = `${inq.parent_first_name || ''} ${inq.parent_last_name || ''}`.trim();
          if (fatherName) updates.fatherName = fatherName;
          if (inq.parent_phone) updates.fatherMobile = inq.parent_phone;
          if (inq.parent_email) updates.fatherEmail = inq.parent_email;
          if (inq.parent_alternate_phone) updates.motherMobile = inq.parent_alternate_phone;
          if (inq.address_line1) updates.streetAddress = inq.address_line1;
          if (inq.city) updates.city = inq.city;
          if (inq.state) updates.state = inq.state;
          if (inq.postal_code) updates.zipCode = inq.postal_code;
          if (inq.primary_diagnosis) updates.primaryConcern = inq.primary_diagnosis;
          const services = inq.preferred_services || {};
          if (services.type) updates.lookingFor = services.type;
        }

        // Fall back to client row for fields the inquiry didn't carry.
        if (!updates.childName && (client?.first_name || client?.last_name)) {
          updates.childName = `${client.first_name || ''} ${client.last_name || ''}`.trim();
        }
        if (!updates.dateOfBirth && client?.date_of_birth) updates.dateOfBirth = client.date_of_birth;
        if (!updates.gender && client?.gender) updates.gender = client.gender;

        updateSharedData(updates);
      } catch (err) {
        console.error('[HydratedReadOnlyForm] hydration failed:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    hydrate();
    return () => {
      cancelled = true;
    };
  }, [childId, updateSharedData, saveFormData, markFormCompleted]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const therapistReturnPath = childId ? `/therapist/child/${childId}` : undefined;
  return Children.map(children, (child) =>
    isValidElement(child) && therapistReturnPath
      ? cloneElement(child, { returnPath: child.props.returnPath ?? therapistReturnPath })
      : child
  );
}

HydratedReadOnlyForm.propTypes = {
  children: PropTypes.node
};
